import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';
import * as Yup from 'yup';

import { CheckmarkIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconColor, Input, Label, Modal, Paragraph, Spinner } from '@treams/treams-ui';

import { WorkspaceBasic } from './types';

const CheckmarkCircle = styled(Flex)`
	border-radius: 50%;
	background-color: ${(props) => props.theme.colors.primary};
	width: 24px;
	height: 24px;
	padding: 4px;
	display: flex;
	div {
		width: 100%;
		height: 100%;
	}
`;

export default function WorkspaceCopyModal(props: {
	workspace: WorkspaceBasic;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const [status, setStatus] = useState<'INIT' | 'LOADING' | 'SUCCESS'>('INIT');

	const formik = useFormik({
		initialValues: {
			name: '',
		},
		validationSchema: Yup.object({
			name: Yup.string().required().min(1),
		}),
		onSubmit: () => {
			update();
		},
	});

	const [update, { loading, data }] = graphql.useCopyWorkspaceMutation({
		variables: {
			input: {
				source: props.workspace.id,
				target: formik.values.name,
			},
		},
	});

	const [checkWorkspace] = graphql.useCheckWorkspaceLazyQuery();

	useEffect(() => {
		if (!data) return;

		formik.setFieldValue('name', data?.copyWorkspace.target || formik.values.name, false);

		if (!data.copyWorkspace.success) {
			return formik.setFieldError('name', data?.copyWorkspace.error || 'common.error');
		}

		setStatus('LOADING');

		const interval = setInterval(async () => {
			const result = await checkWorkspace({ variables: { id: data.copyWorkspace.target } });

			if (result.data?.isWorkspaceSlugAvailable !== false) return;

			setStatus('SUCCESS');
		}, 1000);

		return () => {
			setStatus('INIT');

			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, checkWorkspace, setStatus]);

	return (
		<Modal width={300}>
			<Modal.Header title={t('settings.workspaces.copy.title')} tabs onClose={props.onDismiss} />
			{status === 'INIT' && (
				<Modal.Contents>
					<Label>{t('settings.workspaces.copy.prompt')}:</Label>
					<Input
						error={formik.errors.name}
						value={formik.values.name}
						onChange={(value) => formik.setFieldValue('name', value)}
						name="name"
						autoFocus
					/>
				</Modal.Contents>
			)}
			{status === 'LOADING' && (
				<Modal.Contents>
					<Spinner centered />
				</Modal.Contents>
			)}
			{status === 'SUCCESS' && (
				<Modal.Contents>
					<Flex py={3} justifyContent="center" flexDirection="column" alignItems="center">
						<CheckmarkCircle>
							<IconColor as="div" color="white">
								<CheckmarkIcon />
							</IconColor>
						</CheckmarkCircle>
						<Paragraph mt={2}>{t('common.success')}</Paragraph>
					</Flex>
				</Modal.Contents>
			)}
			<Modal.FormFooter
				small
				onCancel={() => props.onDismiss()}
				onSave={() => {
					if (status === 'SUCCESS') return props.onDismiss();

					formik.handleSubmit();
				}}
				saveTitle={status === 'SUCCESS' ? t('common.close') : t('common.save')}
				disabled={!formik.isValid || loading || status === 'LOADING'}
			/>
		</Modal>
	);
}
